import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const DEFAULT_CAM = _isMobile ? { x: 0, y: 0.93, z: 1.11 } : { x: 0, y: 1.09, z: 1.3 };
const SCALE = 0.008;

function createRoundedRectGeometry(l, w, t, rFL, rFR, rBL, rBR) {
  const shape = new THREE.Shape();
  const halfL = l / 2;
  const halfW = w / 2;
  const maxR = Math.min(halfL, halfW) - 0.001;
  rFL = Math.min(Math.max(rFL, 0), maxR);
  rFR = Math.min(Math.max(rFR, 0), maxR);
  rBL = Math.min(Math.max(rBL, 0), maxR);
  rBR = Math.min(Math.max(rBR, 0), maxR);

  // Shape in XY: X = length, Y- = front (world Z+), Y+ = back (world Z-)
  shape.moveTo(-halfL + rFL, -halfW);
  shape.lineTo(halfL - rFR, -halfW);
  if (rFR > 0.001) shape.quadraticCurveTo(halfL, -halfW, halfL, -halfW + rFR);
  else shape.lineTo(halfL, -halfW);
  shape.lineTo(halfL, halfW - rBR);
  if (rBR > 0.001) shape.quadraticCurveTo(halfL, halfW, halfL - rBR, halfW);
  else shape.lineTo(halfL, halfW);
  shape.lineTo(-halfL + rBL, halfW);
  if (rBL > 0.001) shape.quadraticCurveTo(-halfL, halfW, -halfL, halfW - rBL);
  else shape.lineTo(-halfL, halfW);
  shape.lineTo(-halfL, -halfW + rFL);
  if (rFL > 0.001) shape.quadraticCurveTo(-halfL, -halfW, -halfL + rFL, -halfW);
  else shape.lineTo(-halfL, -halfW);

  const bevel = Math.min(0.006, t * 0.12);
  const geo = new THREE.ExtrudeGeometry(shape, {
    steps: 1, depth: t, bevelEnabled: true,
    bevelThickness: bevel, bevelSize: bevel, bevelSegments: 3
  });
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, -t / 2, 0);
  remapExtrudeUVs(geo);
  geo.computeVertexNormals();
  return geo;
}

const RoundedBackSeat3DPreview = ({ length, width, thickness, cornerRadii = {}, fabricColor, fabricImageUrl, fabricZoom = 2, zipPlacement = 'front', showZip = true, rawLength, rawWidth, rawThickness, unit = 'cm', activeField = null, pipingOption = 'none', pipingColor = null, pipingImageUrl = null }) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const zipGroupRef = useRef(null);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);
  const sceneRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ length: null, width: null, thickness: null });
  const dimLinesRef = useRef(null);
  const activeFieldRef = useRef(activeField);
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const targetScaleYRef = useRef(1);

  const sl = Math.max(length * SCALE, 0.15);
  const sw = Math.max(width * SCALE, 0.1);
  const st = Math.max(thickness * SCALE, 0.03);
  const rFL = (cornerRadii.frontLeft || 0) * SCALE;
  const rFR = (cornerRadii.frontRight || 0) * SCALE;
  const rBL = (cornerRadii.backLeft || 0) * SCALE;
  const rBR = (cornerRadii.backRight || 0) * SCALE;
  const color = fabricColor || '#5a8a5a';

  const fabricImageUrlRef = useRef(fabricImageUrl);
  const colorRef = useRef(color);
  const fabricZoomRef = useRef(fabricZoom);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { fabricImageUrlRef.current = fabricImageUrl; colorRef.current = color; }, [fabricImageUrl, color]);
  useEffect(() => { fabricZoomRef.current = fabricZoom; }, [fabricZoom]);

  useEffect(() => { targetDimsRef.current = { l: sl, w: sw, t: st, rFL, rFR, rBL, rBR }; }, [sl, sw, st, rFL, rFR, rBL, rBR]);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(5, 5), new THREE.ShadowMaterial({ opacity: 0.15 }));
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    ground.receiveShadow = true;
    scene.add(ground);

    const geo = createRoundedRectGeometry(sl, sw, st, rFL, rFR, rBL, rBR);
    const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 });
    const cushion = new THREE.Mesh(geo, mat);
    cushion.castShadow = true;
    scene.add(cushion);
    cushionRef.current = cushion;
    currentDimsRef.current = { l: sl, w: sw, t: st, rFL, rFR, rBL, rBR };

    // Zip group - will be rebuilt on dimension/placement changes
    const zipGroup = new THREE.Group();
    scene.add(zipGroup);
    zipGroupRef.current = zipGroup;

    // Dimension lines: A (length, horizontal) and Depth (width, right side)
    const lengthMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const depthMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dimArrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);

    // A line: horizontal dashes across the top
    const aDashes = [];
    for (let i = 0; i < 24; i++) {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), lengthMat);
      scene.add(d);
      aDashes.push(d);
    }
    const aA1 = new THREE.Mesh(dimArrowGeo, lengthMat); scene.add(aA1);
    const aA2 = new THREE.Mesh(dimArrowGeo, lengthMat); scene.add(aA2);

    // Depth line: vertical dashes on the right side
    const dDashes = [];
    for (let i = 0; i < 20; i++) {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), depthMat);
      scene.add(d);
      dDashes.push(d);
    }
    const dA1 = new THREE.Mesh(dimArrowGeo, depthMat); scene.add(dA1);
    const dA2 = new THREE.Mesh(dimArrowGeo, depthMat); scene.add(dA2);

    dimLinesRef.current = { aDashes, aA1, aA2, dDashes, dA1, dA2, lengthMat, depthMat };

    // Piping rings (top + bottom)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;
    sceneRef.current = scene;

    // Trigger auto-zoom on first load so it matches reset button
    resetCameraRef.current = true;

    const targetCamPos = new THREE.Vector3();
    const zeroVec = new THREE.Vector3(0, 0, 0);
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = dimLinesRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });

              if (zipGroupRef.current) zipGroupRef.current.visible = false;
}
      controls.update();
      const td = targetDimsRef.current;
      let cd = currentDimsRef.current;
      if (cd && td) {
        const lerpSpeed = 0.1;
        const changed = Math.abs(cd.l - td.l) > 0.0001 || Math.abs(cd.w - td.w) > 0.0001 || Math.abs(cd.t - td.t) > 0.0001 || Math.abs(cd.rFL - td.rFL) > 0.0001 || Math.abs(cd.rFR - td.rFR) > 0.0001 || Math.abs(cd.rBL - td.rBL) > 0.0001 || Math.abs(cd.rBR - td.rBR) > 0.0001;
        if (changed) {
          cd.l += (td.l - cd.l) * lerpSpeed;
          cd.w += (td.w - cd.w) * lerpSpeed;
          cd.t += (td.t - cd.t) * lerpSpeed;
          cd.rFL += (td.rFL - cd.rFL) * lerpSpeed;
          cd.rFR += (td.rFR - cd.rFR) * lerpSpeed;
          cd.rBL += (td.rBL - cd.rBL) * lerpSpeed;
          cd.rBR += (td.rBR - cd.rBR) * lerpSpeed;
          cushion.geometry.dispose();
          cushion.geometry = createRoundedRectGeometry(cd.l, cd.w, cd.t, cd.rFL, cd.rFR, cd.rBL, cd.rBR);
          applyFabricTexture(cushion, fabricImageUrlRef.current, colorRef.current, fabricZoomRef.current);
        }
        ground.position.y = -cd.t / 2 - 0.002;

        // Piping Y tracking
        if (pipingTopRef.current && pipingTopRef.current.visible) {
          const pBevel = Math.min(0.006, cd.t * 0.12);
          const pY = cd.t / 2 + pBevel * Math.SQRT1_2;
          pipingTopRef.current.position.y = pY;
          pipingBottomRef.current.position.y = -pY;
        }
        if (resetCameraRef.current) {
          const maxDim = Math.max(cd.l, cd.w);
          const isMobile = containerWidthRef.current < 500;
          const distMult = isMobile ? 1.3 : 1.35;
          const distBase = isMobile ? 0.3 : 0.3;
          const camScale = isMobile ? 1.3 : 1.7;
          const dist = maxDim * distMult + distBase;
          targetCamPos.set(DEFAULT_CAM.x / camScale * dist, DEFAULT_CAM.y / camScale * dist, DEFAULT_CAM.z / camScale * dist);
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.lerp(zeroVec, 0.15);
          if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
        }
      }
      renderer.render(scene, camera);
      const af = activeFieldRef.current;
      const YELLOW = 0xffd700;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      // Position dimension lines
      const dl = dimLinesRef.current;
      if (cd && dl) {
        const bev = Math.min(0.006, cd.t * 0.12);
        const topY = cd.t / 2 + bev + 0.004;
        const sY = cushion.scale.y;

        // A line (length): horizontal across top center
        const halfL = cd.l / 2;
        for (let i = 0; i < dl.aDashes.length; i++) {
          const t = (i + 0.5) / dl.aDashes.length;
          dl.aDashes[i].position.set(-halfL + cd.l * t, topY * sY, 0);
        }
        dl.aA1.position.set(-halfL, topY * sY, 0);
        dl.aA1.rotation.set(0, 0, Math.PI / 2);
        dl.aA2.position.set(halfL, topY * sY, 0);
        dl.aA2.rotation.set(0, 0, -Math.PI / 2);

        // Depth line: vertical on the right side
        const halfW = cd.w / 2;
        const lineX = cd.l * 0.15;
        for (let i = 0; i < dl.dDashes.length; i++) {
          const t = (i + 0.5) / dl.dDashes.length;
          dl.dDashes[i].position.set(lineX, topY * sY, -halfW + cd.w * t);
        }
        dl.dA1.position.set(lineX, topY * sY, -halfW);
        dl.dA1.rotation.set(-Math.PI / 2, 0, 0);
        dl.dA2.position.set(lineX, topY * sY, halfW);
        dl.dA2.rotation.set(Math.PI / 2, 0, 0);

        // Highlight line colors
        dl.lengthMat.color.set(af === 'length' ? YELLOW : 0xffffff);
        dl.depthMat.color.set(af === 'width' ? YELLOW : 0xffffff);
      }

      // Position + highlight labels
      if (cd && dimLabelsRef.current.length && dimLabelsRef.current.width && dimLabelsRef.current.thickness) {
        const cr = container.getBoundingClientRect();
        const proj = (wp) => { const v = wp.clone().project(camera); return { x: (v.x * 0.5 + 0.5) * cr.width, y: (-v.y * 0.5 + 0.5) * cr.height, visible: v.z < 1 }; };
        const bev = Math.min(0.006, cd.t * 0.12);
        const labelY = cd.t / 2 + bev + 0.008;

        // A label: front edge, center
        const lp = proj(new THREE.Vector3(0, labelY, cd.w / 2 + 0.03));
        Object.assign(dimLabelsRef.current.length.style, { left: lp.x + 'px', top: lp.y + 'px', opacity: lp.visible ? '1' : '0', backgroundColor: af === 'length' ? activeBg : defaultBg, color: af === 'length' ? activeText : defaultText });

        // Depth label: right side, next to the depth line
        const wp2 = proj(new THREE.Vector3(cd.l * 0.15 + 0.03, labelY, 0));
        Object.assign(dimLabelsRef.current.width.style, { left: wp2.x + 'px', top: wp2.y + 'px', opacity: wp2.visible ? '1' : '0', backgroundColor: af === 'width' ? activeBg : defaultBg, color: af === 'width' ? activeText : defaultText });

        // Thickness label: front-left corner
        const tp = proj(new THREE.Vector3(-cd.l / 2 - 0.02, 0, -cd.w / 2 - 0.02));
        Object.assign(dimLabelsRef.current.thickness.style, { left: tp.x + 'px', top: tp.y + 'px', opacity: tp.visible ? '1' : '0', backgroundColor: af === 't' ? activeBg : defaultBg, color: af === 't' ? activeText : defaultText });
      }
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cushionRef.current) return;
    targetDimsRef.current = { l: sl, w: sw, t: st, rFL, rFR, rBL, rBR };
    resetCameraRef.current = true;
  }, [sl, sw, st, rFL, rFR, rBL, rBR]);

  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
  }, [color, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings — trace the rounded rectangle outline on top/bottom faces
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    const halfL = sl / 2;
    const halfW = sw / 2;
    const maxR = Math.min(halfL, halfW) - 0.001;
    const cFL = Math.min(Math.max(rFL, 0), maxR);
    const cFR = Math.min(Math.max(rFR, 0), maxR);
    const cBL = Math.min(Math.max(rBL, 0), maxR);
    const cBR = Math.min(Math.max(rBR, 0), maxR);
    const bevel = Math.min(0.006, st * 0.12);
    const tubeRadius = 0.003;
    const expansion = bevel * Math.SQRT1_2;

    // Reconstruct the same rounded rectangle shape as createRoundedRectGeometry
    const shape = new THREE.Shape();
    shape.moveTo(-halfL + cFL, -halfW);
    shape.lineTo(halfL - cFR, -halfW);
    if (cFR > 0.001) shape.quadraticCurveTo(halfL, -halfW, halfL, -halfW + cFR);
    else shape.lineTo(halfL, -halfW);
    shape.lineTo(halfL, halfW - cBR);
    if (cBR > 0.001) shape.quadraticCurveTo(halfL, halfW, halfL - cBR, halfW);
    else shape.lineTo(halfL, halfW);
    shape.lineTo(-halfL + cBL, halfW);
    if (cBL > 0.001) shape.quadraticCurveTo(-halfL, halfW, -halfL, halfW - cBL);
    else shape.lineTo(-halfL, halfW);
    shape.lineTo(-halfL, -halfW + cFL);
    if (cFL > 0.001) shape.quadraticCurveTo(-halfL, -halfW, -halfL + cFL, -halfW);
    else shape.lineTo(-halfL, -halfW);

    const pts2D = shape.getPoints(64);
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();

    // Convert to world XZ coordinates: shape X → world X, shape Y → world -Z
    const pts3D = pts2D.map(p => new THREE.Vector3(p.x, 0, -p.y));

    // Expand outward by bevel*cos(45°) so piping sits ON the bevel surface
    const n = pts3D.length;
    const expanded = pts3D.map((pt3, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next = pts3D[(i + 1) % n];
      const dx = next.x - prev.x;
      const dz = next.z - prev.z;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      // Outward normal in XZ plane (perpendicular to tangent, pointing away from center)
      const nx = -dz / len;
      const nz = dx / len;
      // Ensure normal points outward (away from origin)
      const dot = nx * pt3.x + nz * pt3.z;
      const sign = dot >= 0 ? 1 : -1;
      return new THREE.Vector3(pt3.x + sign * nx * expansion, 0, pt3.z + sign * nz * expansion);
    });

    // LinearClosedCurve — linear interpolation, closed loop
    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n2 = this.pts.length;
        const f = t * n2;
        const i = Math.floor(f) % n2;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n2], a);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    const tubeGeo = new THREE.TubeGeometry(curve, 128, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    const pipingY = st / 2 + bevel * Math.SQRT1_2;
    pt.position.y = pipingY;
    pb.position.y = -pipingY;
  }, [sl, sw, st, rFL, rFR, rBL, rBR, pipingOption, pipingColor, pipingImageUrl, fabricColor, fabricImageUrl, fabricZoom]);

  useEffect(() => {
    if (!zipGroupRef.current) return;
    zipGroupRef.current.visible = showZip;
    if (!showZip) return;
    let lastKey = '';
    const buildZip = () => {
      const cd = currentDimsRef.current;
      if (!cd) return;
      const key = [cd.l, cd.w, cd.t, cd.rFL, cd.rFR, cd.rBL, cd.rBR, zipPlacement].join(',');
      if (key === lastKey) return;
      lastKey = key;

      const zg = zipGroupRef.current;
      while (zg.children.length) {
        const ch = zg.children[0];
        if (ch.geometry) ch.geometry.dispose();
        if (ch.material) ch.material.dispose();
        zg.remove(ch);
      }

      const halfL = cd.l / 2, halfW = cd.w / 2;
      const isBack = zipPlacement === 'back';

      // CRITICAL: Clamp radii exactly like createRoundedRectGeometry does
      const maxR = Math.min(halfL, halfW) - 0.001;
      const cBL = Math.min(Math.max(cd.rBL, 0), maxR);
      const cBR = Math.min(Math.max(cd.rBR, 0), maxR);
      const cFL = Math.min(Math.max(cd.rFL, 0), maxR);
      const cFR = Math.min(Math.max(cd.rFR, 0), maxR);

      // Quadratic bezier helper
      const qB = (a, c, b, t) => {
        const m = 1 - t;
        return [m * m * a[0] + 2 * m * t * c[0] + t * t * b[0],
                m * m * a[1] + 2 * m * t * c[1] + t * t * b[1]];
      };

      const N = 32;
      const ePts = [];

      if (isBack) {
        // 50% of back-left curve: t from 0.5 to 0
        if (cBL > 0.001) {
          for (let i = 0; i <= N; i++) {
            const t = 0.5 * (1 - i / N);
            ePts.push(qB([-halfL + cBL, halfW], [-halfL, halfW], [-halfL, halfW - cBL], t));
          }
        } else {
          ePts.push([-halfL, halfW]);
        }
        // Straight back edge
        const sL = -halfL + cBL, sR = halfL - cBR;
        if (sR > sL + 0.0005) {
          const n = Math.max(2, Math.round((sR - sL) / 0.006));
          for (let i = 0; i <= n; i++) ePts.push([sL + (sR - sL) * i / n, halfW]);
        }
        // 50% of back-right curve: t from 1.0 to 0.5
        if (cBR > 0.001) {
          for (let i = 0; i <= N; i++) {
            const t = 1.0 - 0.5 * (i / N);
            ePts.push(qB([halfL, halfW - cBR], [halfL, halfW], [halfL - cBR, halfW], t));
          }
        } else {
          ePts.push([halfL, halfW]);
        }
      } else {
        // 50% of front-left curve
        if (cFL > 0.001) {
          for (let i = 0; i <= N; i++) {
            const t = 0.5 + 0.5 * (i / N);
            ePts.push(qB([-halfL, -halfW + cFL], [-halfL, -halfW], [-halfL + cFL, -halfW], t));
          }
        } else {
          ePts.push([-halfL, -halfW]);
        }
        // Straight front edge
        const sL = -halfL + cFL, sR = halfL - cFR;
        if (sR > sL + 0.0005) {
          const n = Math.max(2, Math.round((sR - sL) / 0.006));
          for (let i = 0; i <= n; i++) ePts.push([sL + (sR - sL) * i / n, -halfW]);
        }
        // 50% of front-right curve
        if (cFR > 0.001) {
          for (let i = 0; i <= N; i++) {
            const t = 0.5 * (i / N);
            ePts.push(qB([halfL - cFR, -halfW], [halfL, -halfW], [halfL, -halfW + cFR], t));
          }
        } else {
          ePts.push([halfL, -halfW]);
        }
      }

      if (ePts.length < 2) return;

      // Deduplicate consecutive points that are too close
      const deduped = [ePts[0]];
      for (let i = 1; i < ePts.length; i++) {
        const dx = ePts[i][0] - deduped[deduped.length - 1][0];
        const dy = ePts[i][1] - deduped[deduped.length - 1][1];
        if (Math.sqrt(dx * dx + dy * dy) > 0.0002) deduped.push(ePts[i]);
      }
      if (deduped.length < 2) return;

      // Convert shape coords to 3D: (sx, sy) -> (sx, 0, -sy)
      const p3 = deduped.map(([sx, sy]) => new THREE.Vector3(sx, 0, -sy));

      // Offset each point outward from the cushion surface
      const OFF = 0.008;
      for (let i = 0; i < p3.length; i++) {
        const prev = p3[Math.max(0, i - 1)];
        const next = p3[Math.min(p3.length - 1, i + 1)];
        const tan = new THREE.Vector3().subVectors(next, prev).normalize();
        const n = new THREE.Vector3(-tan.z, 0, tan.x);
        if (n.dot(p3[i]) < 0) n.negate();
        p3[i].addScaledVector(n, OFF);
      }

      // Smooth curve through offset points
      const curve = new THREE.CatmullRomCurve3(p3, false, 'centripetal', 0.5);

      // Zip track (tube along the curve)
      const trackGeo = new THREE.TubeGeometry(curve, Math.max(48, deduped.length), 0.0035, 6, false);
      const trackMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      zg.add(new THREE.Mesh(trackGeo, trackMat));

      // Zip teeth along the curve
      const toothMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const totalLen = curve.getLength();
      const spacing = 0.014;
      const nTeeth = Math.floor(totalLen / spacing);
      for (let i = 0; i < nTeeth; i++) {
        const frac = (i + 0.5) / nTeeth;
        const pos = curve.getPoint(frac);
        const tan = curve.getTangent(frac);
        const normal = new THREE.Vector3(-tan.z, 0, tan.x).normalize();
        const side = (i % 2 === 0) ? 1 : -1;
        const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.005, 0.004), toothMat);
        tooth.position.copy(pos).addScaledVector(normal, side * 0.007);
        tooth.rotation.y = -Math.atan2(tan.z, tan.x);
        zg.add(tooth);
      }
    };
    buildZip();
    const interval = setInterval(buildZip, 200);
    return () => clearInterval(interval);
  }, [zipPlacement]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="rounded-back-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="three-canvas-container" />
      <div ref={el => dimLabelsRef.current.length = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-length">{rawLength > 0 ? `${unit === 'mm' ? rawLength * 10 : rawLength} ${unit}` : `A ${unit}`}</div>
      <div ref={el => dimLabelsRef.current.width = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-width">{rawWidth > 0 ? `${unit === 'mm' ? rawWidth * 10 : rawWidth} ${unit}` : `D ${unit}`}</div>
      <div ref={el => dimLabelsRef.current.thickness = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0, transition: 'opacity 0.15s' }} data-testid="dim-label-thickness">{rawThickness ? `${unit === 'mm' ? rawThickness * 10 : rawThickness} ${unit} thick` : `T ${unit}`}</div>
      {showZip && (
      <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
        <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
        <span className="font-medium">Zip placement</span>
      </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default RoundedBackSeat3DPreview;
